import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import styled from 'styled-components';

const ProfileHeader = styled(Box)`
  background: linear-gradient(45deg, #2196f3 30%, #21cbf3 90%);
  color: white;
  padding: 40px 0;
  margin-bottom: 40px;
`;

const ProfileImage = styled.img`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
`;

const ServiceImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  &:hover {
    transform: scale(1.05);
  }
`;

const ContactButton = styled(Button)`
  margin: 8px;
`;

const ImagePreview = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
  margin: 8px;
`;

const ImageUploadContainer = styled(Box)`
  border: 2px dashed #90caf9;
  padding: 20px;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  background: #f5faff;
  &:hover {
    border-color: #2196f3;
    background: #e3f2fd;
  }
`;

const professions = [
  'Pedreiro',
  'Ajudante de Pedreiro',
  'Doméstica',
  'Cuidador de Idoso',
  'Babá',
  'Capinador',
  'Bombeiro',
  'Eletricista',
  'Carpinteiro',
  'Serralheiro',
  'Montador de Moveis',
  'Segurança',
  'Garçon',
  'Entregador',
  'Professo Particular',
  'Pintor',
  'Programador',
  'Fotógrafo',
  'Personal Trainner',
  'Contador',
  'Gesseiro',
];

const ProviderProfile = () => {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openContact, setOpenContact] = useState(false);

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const response = await fetch(`/api/providers-profile?id=${id}`);
        if (!response.ok) throw new Error('Erro ao carregar perfil');
        const data = await response.json();
        setProvider(data);
      } catch (error) {
        setError('Erro ao carregar perfil do profissional');
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [id]);

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!provider) {
    return (
      <Container>
        <Alert severity="error">Profissional não encontrado</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 5, mb: 5 }}>
        <Card sx={{ borderRadius: 4, boxShadow: 6, background: '#fafdff' }}>
          <CardContent>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <ProfileImage
                src={provider.profileImage || '/default-profile.png'}
                alt={provider.name}
                onClick={() => setSelectedImage(provider.profileImage)}
              />
              <Typography variant="h4" fontWeight={700} color="#1976d2" align="center" gutterBottom sx={{ mt: 2 }}>
                {provider.name}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {provider.profession}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Typography variant="body1" color="text.secondary">
                  <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {provider.address?.neighborhood ? `${provider.address.neighborhood}, ` : ''}
                  {provider.address?.city || provider.city}, {provider.address?.state || provider.state}
                </Typography>
                <Box display="flex" alignItems="center" color="text.secondary">
                  <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2">{provider.views || 0} visualizações</Typography>
                </Box>
              </Box>
              {provider.rating && (
                <Box display="flex" alignItems="center" mt={1}>
                  <Rating value={provider.rating} readOnly precision={0.5} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({provider.reviews?.length || 0} avaliações)
                  </Typography>
                </Box>
              )}
            </Box>

            <Box mb={4}>
              <Typography variant="h6" gutterBottom color="primary">
                Sobre
              </Typography>
              <Typography variant="body1" paragraph>
                {provider.description || 'Este profissional ainda não adicionou uma descrição.'}
              </Typography>
            </Box>

            {provider.serviceImages && provider.serviceImages.length > 0 && (
              <Box mb={4}>
                <Typography variant="h6" gutterBottom color="primary">
                  Portfólio de Trabalhos
                </Typography>
                <Grid container spacing={2}>
                  {provider.serviceImages.map((image, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <ServiceImage
                        src={image}
                        alt={`Trabalho ${index + 1}`}
                        onClick={() => setSelectedImage(image)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<WorkIcon />}
                onClick={() => setOpenContact(true)}
              >
                Contatar Profissional
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Modal de contato */}
      <Dialog open={openContact} onClose={() => setOpenContact(false)}>
        <DialogTitle>Contato do Profissional</DialogTitle>
        <DialogContent>
          <Typography variant="h6">{provider.name}</Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Telefone: <strong>{provider.phone}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContact(false)} color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Imagem ampliada"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ProviderProfile; 